// src/oura/merchant/setup/Questions.tsx
//
// "Your business" answered on the Setup page itself (one setup), in place of
// the Google Form. The questions come from SETUP_QUESTIONS, in its order.
//
// One list, in SETUP_QUESTIONS order. What Oura already knows is filled in
// before they arrive and carries a small sparkle (its tooltip says so), so the
// merchant checks rather than types; the mark goes once they change it. The
// send bar stays in view at the bottom. Every change saves on its own a moment
// later, so they can stop half way and come back from the same link. Words
// typed in a list but not yet added count as added: nobody loses an answer
// for not pressing Enter. Sending
// checks the answers that hold the step, says which are missing next to
// each one, and moves the page on once they land.

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { ArrowRightIcon, CheckIcon, Loader2Icon, SparklesIcon } from "lucide-react";
import { Tooltip } from "@component/tooltip";
import { cn } from "../../lib/cn";
import { FOCUS_RING, PillButton, TYPE } from "@oura/ui";
import { saveMyAnswers, submitMyAnswers, type MerchantOnboarding, type SetupAnswerKey, type SetupAnswers } from "@service/aeo/onboarding";
import { addEntries, answeredCount, cleanAnswers, isAnswered, isEmailLike, missingAnswers, SETUP_QUESTIONS, withDefaults, type SetupQuestion } from "@oura/onboarding/setupQuestions";
import { ChoiceInput, ListInput } from "./AnswerInputs";

/** How long after the last change the answers save. */
const SAVE_MS = 800;
const TAP = "max-sm:h-9";
/** 16px on a phone: smaller text makes iPhones zoom in on a tap. */
const FIELD = "w-full rounded-xl bg-white px-3 text-base text-gray-900 ring-1 ring-inset placeholder:text-gray-400 sm:text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600";

type SaveState = "idle" | "saving" | "saved" | "failed";
type Errors = Partial<Record<SetupAnswerKey, string>>;
/** What is typed in a list's box and not yet added, by question. */
type Typed = Partial<Record<SetupAnswerKey, string>>;

/** The answers with whatever is typed but not yet added, as they are counted, saved and sent. */
function withTyped(answers: SetupAnswers, typed: Typed): SetupAnswers {
    const out = { ...answers };
    for (const key of Object.keys(typed) as SetupAnswerKey[]) {
        const list = out[key];
        const text = typed[key] ?? "";
        if (Array.isArray(list) && text.trim()) (out as Record<SetupAnswerKey, string[] | string>)[key] = addEntries(list, text);
    }
    return out;
}

/** What the page says under an answer the send found missing. */
const MISSING: Record<SetupQuestion["kind"], string> = {
    list: "Add at least one.",
    choices: "Pick at least one.",
    line: "Add a name.",
    text: "Add a few words.",
    person: "Add their name and an email we can write to.",
};

export function Questions({
    onboarding,
    readOnly,
    onBehalf = false,
    onChanged,
}: {
    onboarding: MerchantOnboarding;
    readOnly: boolean;
    /** Staff are filling this in for the merchant, from a view-as session (#4497). */
    onBehalf?: boolean;
    onChanged: (updated: MerchantOnboarding, advance: boolean) => void;
}) {
    const form = onboarding.form ?? null;
    const [answers, setAnswers] = useState<SetupAnswers>(() => withDefaults(form?.draft));
    const [typed, setTyped] = useState<Typed>({});
    const [errors, setErrors] = useState<Errors>({});
    // The answers still as Oura filled them in. One loses its mark the moment the merchant changes it.
    const [fromUs, setFromUs] = useState<Set<SetupAnswerKey>>(() => new Set(form?.prefilled ?? []));
    const [failure, setFailure] = useState<string | null>(null);
    const [sending, setSending] = useState(false);
    const [save, setSave] = useState<SaveState>(form?.savedAt ? "saved" : "idle");
    const id = useId();
    const fieldId = (key: SetupAnswerKey) => `${id}-${key}`;

    const latest = useRef(answers);
    const latestTyped = useRef(typed);
    const dirty = useRef(new Set<SetupAnswerKey>());
    const timer = useRef<number | null>(null);
    /** The save on its way, if any. */
    const inFlight = useRef<Promise<unknown> | null>(null);
    const sent = useRef(false);
    const report = useRef(onChanged);
    report.current = onChanged;

    /** Save what changed since the last save. One save at a time; changes made meanwhile go in the next. */
    const flush = useCallback(async () => {
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = null;
        if (readOnly || sent.current || inFlight.current || dirty.current.size === 0) return;
        const keys = Array.from(dirty.current);
        dirty.current.clear();
        const all = withTyped(latest.current, latestTyped.current);
        const partial = Object.fromEntries(keys.map((k) => [k, all[k]])) as Partial<SetupAnswers>;
        setSave("saving");
        const saving = saveMyAnswers(partial);
        inFlight.current = saving.catch(() => null);
        try {
            const updated = await saving;
            // A save that lands after the answers were sent would put the step back to open.
            if (!sent.current) report.current(updated, false);
            setSave("saved");
        } catch {
            keys.forEach((k) => dirty.current.add(k));
            setSave("failed");
        } finally {
            inFlight.current = null;
            if (dirty.current.size > 0 && !sent.current) timer.current = window.setTimeout(() => void flush(), SAVE_MS);
        }
    }, [readOnly]);

    // Leaving the step, or the page, keeps what was typed.
    useEffect(() => () => void flush(), [flush]);

    const changed = (key: SetupAnswerKey) => {
        dirty.current.add(key);
        // The approver's email belongs to the approver question: changing it is changing that answer.
        const mark = key === "approverEmail" ? "approver" : key;
        if (fromUs.has(mark)) setFromUs((was) => without(was, mark));
        if (errors[key] && isAnswered(withTyped(latest.current, latestTyped.current), key)) setErrors((was) => ({ ...was, [key]: undefined }));
        setFailure(null);
        if (timer.current) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => void flush(), SAVE_MS);
    };
    const change = <K extends SetupAnswerKey>(key: K, value: SetupAnswers[K]) => {
        latest.current = { ...latest.current, [key]: value };
        setAnswers(latest.current);
        changed(key);
    };
    const type = (key: SetupAnswerKey, text: string) => {
        latestTyped.current = { ...latestTyped.current, [key]: text };
        setTyped(latestTyped.current);
        changed(key);
    };

    const send = async () => {
        // Once only: a second press while the first is on its way does nothing.
        if (readOnly || sent.current) return;
        const clean = cleanAnswers(withTyped(latest.current, latestTyped.current));
        const missing = missingAnswers(clean);
        if (missing.length > 0) {
            setErrors(Object.fromEntries(missing.map((q) => [q.key, q.kind === "person" && clean.approverEmail === undefined ? "Add their name." : MISSING[q.kind]])));
            setFailure(missing.length === 1 ? "One answer is missing." : `${missing.length} answers are missing.`);
            const first = missing[0].key;
            const toFocus = first === "approver" && clean.approver ? `${fieldId(first)}-email` : fieldId(first);
            document.getElementById(toFocus)?.focus();
            return;
        }
        if (timer.current) window.clearTimeout(timer.current);
        sent.current = true;
        setSending(true);
        setFailure(null);
        // A save still on its way lands before the send, never after it. The
        // backend also ignores a save once the answers are in (PATCH after
        // `answeredAt` changes nothing, and a second send returns the record
        // unchanged), so neither order can reopen the step.
        await inFlight.current;
        const result = await submitMyAnswers(clean).catch(() => null);
        setSending(false);
        if (result?.ok) {
            dirty.current.clear();
            onChanged(result.onboarding, true);
            return;
        }
        sent.current = false;
        if (!result) {
            setFailure("Your answers were not sent. Try again in a moment.");
            return;
        }
        setErrors(result.fields);
        setFailure(result.message);
        const first = SETUP_QUESTIONS.find((q) => result.fields[q.key]);
        if (first) document.getElementById(fieldId(first.key))?.focus();
    };

    const now = withTyped(answers, typed);
    const { done, total } = answeredCount(now);
    const left = total - done;
    // The first required answer still missing: named in the send bar, one press away.
    const nextMissing = missingAnswers(now)[0] ?? null;
    const goTo = (key: SetupAnswerKey) => {
        const target = key === "approver" && answers.approver.trim() ? `${fieldId(key)}-email` : fieldId(key);
        const el = document.getElementById(target) ?? document.getElementById(`${fieldId(key)}-title`);
        el?.scrollIntoView({ block: "center", behavior: "smooth" });
        el?.focus({ preventScroll: true });
    };

    return (
        <div className="space-y-6">
            {onBehalf && (
                <p role="note" className={cn(TYPE.tc, "m-0 rounded-xl bg-blue-50 px-3 py-2 text-blue-900")}>
                    You are answering for the client. Saving and sending emails no one, and the client sees these as their answers.
                </p>
            )}
            <ol className="list-plain m-0 space-y-6 p-0 [&_input]:scroll-mb-32 [&_textarea]:scroll-mb-32">
                {SETUP_QUESTIONS.map((q, i) => (
                    <li key={q.key}>
                        <Question
                            n={i + 1}
                            q={q}
                            id={fieldId(q.key)}
                            answers={answers}
                            typed={typed[q.key] ?? ""}
                            fromUs={fromUs.has(q.key)}
                            error={errors[q.key]}
                            readOnly={readOnly}
                            onChange={change}
                            onType={(text) => type(q.key, text)}
                        />
                    </li>
                ))}
            </ol>

            {/* A solid page-ground tray under the bar, down to the screen's bottom edge:
                nothing scrolls through a gap below it or pokes out beside it. */}
            <div className="sticky bottom-0 z-10 -mx-2 rounded-2xl bg-[#F5F5F7] p-2 pb-[max(8px,env(safe-area-inset-bottom))]">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl bg-white px-4 py-3 ring-1 ring-black/[0.08]">
                <div className="min-w-0 flex-1">
                    <p className={cn(TYPE.tb, "m-0 font-medium tabular-nums text-gray-900")}>
                        {left === 0 ? "All set. Send when you're ready." : left === 1 ? "1 answer to go" : `${left} answers to go`}
                        {nextMissing && (
                            <span className="max-sm:hidden">
                                {": "}
                                <button
                                    type="button"
                                    onClick={() => goTo(nextMissing.key)}
                                    className={cn("rounded font-medium text-blue-600 underline-offset-2 hover:underline", FOCUS_RING)}
                                >
                                    {nextMissing.title}
                                </button>
                            </span>
                        )}
                    </p>
                    <span className="max-sm:hidden">
                        <SaveNote state={save} />
                    </span>
                    {nextMissing && (
                        <button type="button" onClick={() => goTo(nextMissing.key)} className={cn(TYPE.tc, "rounded text-blue-600 sm:hidden", FOCUS_RING)}>
                            Show me
                        </button>
                    )}
                </div>
                <PillButton variant="primary" busy={sending} disabled={readOnly} className={TAP} onClick={() => void send()}>
                    Send answers
                    <ArrowRightIcon aria-hidden size={14} />
                </PillButton>
                {failure && (
                    <p role="alert" className={cn(TYPE.tc, "m-0 w-full font-medium text-rose-600")}>
                        {failure}
                    </p>
                )}
            </div>
            </div>
        </div>
    );
}

function without<T>(set: Set<T>, item: T): Set<T> {
    const next = new Set(set);
    next.delete(item);
    return next;
}

function SaveNote({ state }: { state: SaveState }) {
    if (state === "idle") return <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Saved as you type.</p>;
    return (
        <p aria-live="polite" className={cn(TYPE.tc, "m-0 inline-flex items-center gap-1.5", state === "failed" ? "text-amber-700" : "text-gray-500")}>
            {state === "saving" && <Loader2Icon aria-hidden size={12} className="animate-spin" />}
            {state === "saved" && <CheckIcon aria-hidden size={12} strokeWidth={3} className="text-emerald-600" />}
            {state === "saving" ? "Saving" : state === "saved" ? "Saved. Come back any time." : "Not saved yet. We try again as you type."}
        </p>
    );
}

function Question({
    n,
    q,
    id,
    answers,
    typed,
    fromUs,
    error,
    readOnly,
    onChange,
    onType,
}: {
    /** Its place in the list, said before the question. */
    n: number;
    q: SetupQuestion;
    id: string;
    answers: SetupAnswers;
    typed: string;
    fromUs: boolean;
    error: string | undefined;
    readOnly: boolean;
    onChange: <K extends SetupAnswerKey>(key: K, value: SetupAnswers[K]) => void;
    onType: (text: string) => void;
}) {
    const titleId = `${id}-title`;
    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;
    const describedBy = cn(hintId, error && errorId);
    const value = answers[q.key];
    const Title = q.kind === "choices" ? "p" : "label";
    // A name and an email are two fields: the title labels the name, the email carries its own label.
    return (
        <div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Title id={titleId} {...(Title === "label" ? { htmlFor: id } : {})} className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>
                    <span aria-hidden className="mr-1.5 tabular-nums text-gray-400">{n}.</span>
                    {q.title}
                </Title>
                {fromUs && (
                    <Tooltip side="top" asChild content="Filled in by us from what you told us before. Change it if it's off.">
                        <button type="button" aria-label="Filled in by us. Change it if it's off." className={cn("rounded-full text-blue-500 hover:text-blue-700", FOCUS_RING)}>
                            <SparklesIcon aria-hidden size={14} />
                        </button>
                    </Tooltip>
                )}
                {!q.required && <span className={cn(TYPE.tm, "text-gray-400")}>Optional</span>}
            </div>
            <p id={hintId} className={cn(TYPE.tc, "m-0 mb-2 mt-0.5 text-gray-500")}>
                {q.hint}
            </p>
            {q.kind === "list" && (
                <ListInput id={id} q={q} value={value as string[]} text={typed} onText={onType} error={Boolean(error)} describedBy={describedBy} readOnly={readOnly} onChange={(v) => onChange(q.key, v)} />
            )}
            {q.kind === "choices" && (
                <ChoiceInput id={id} q={q} labelledBy={titleId} value={value as string[]} text={typed} onText={onType} describedBy={describedBy} readOnly={readOnly} onChange={(v) => onChange(q.key, v)} />
            )}
            {q.kind === "line" && (
                <input
                    id={id}
                    value={value as string}
                    placeholder={q.placeholder}
                    autoComplete="name"
                    readOnly={readOnly}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy}
                    onChange={(e) => onChange(q.key, e.target.value)}
                    className={cn(FIELD, "h-10 max-w-sm", error ? "ring-rose-600" : "ring-black/[0.08]")}
                />
            )}
            {q.kind === "person" && (
                <div className={cn("grid grid-cols-1 gap-2", answers.approverEmail === undefined ? "max-w-sm" : "max-w-xl sm:grid-cols-2")}>
                    <input
                        id={id}
                        value={value as string}
                        placeholder={q.placeholder}
                        aria-label="Their name"
                        autoComplete="name"
                        readOnly={readOnly}
                        aria-invalid={error && !(value as string).trim() ? true : undefined}
                        aria-describedby={describedBy}
                        onChange={(e) => onChange(q.key, e.target.value)}
                        className={cn(FIELD, "h-10", error && !(value as string).trim() ? "ring-rose-600" : "ring-black/[0.08]")}
                    />
                    {/* Only where the server asks for it: an older backend takes the name alone. */}
                    {answers.approverEmail !== undefined && (
                        <input
                            id={`${id}-email`}
                            type="email"
                            inputMode="email"
                            value={answers.approverEmail}
                            placeholder="Their email"
                            aria-label="Their email"
                            autoComplete="email"
                            readOnly={readOnly}
                            aria-invalid={error && !isEmailLike(answers.approverEmail) ? true : undefined}
                            aria-describedby={describedBy}
                            onChange={(e) => onChange("approverEmail", e.target.value)}
                            className={cn(FIELD, "h-10", error && !isEmailLike(answers.approverEmail) ? "ring-rose-600" : "ring-black/[0.08]")}
                        />
                    )}
                </div>
            )}
            {q.kind === "text" && (
                <textarea
                    id={id}
                    value={value as string}
                    placeholder={q.placeholder}
                    rows={2}
                    readOnly={readOnly}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy}
                    onChange={(e) => onChange(q.key, e.target.value)}
                    className={cn(FIELD, "block min-h-[72px] resize-y py-2.5 [field-sizing:content]", error ? "ring-rose-600" : "ring-black/[0.08]")}
                />
            )}
            {error && (
                <p id={errorId} className={cn(TYPE.tc, "m-0 mt-1.5 font-medium text-rose-600")}>
                    {error}
                </p>
            )}
        </div>
    );
}
